// 레이드 VFX 바닥 판(raidVfx.ts가 쓴다): 가산(빛) 한 벌 + 알파(그을음·흙) 한 벌, 각각 InstancedMesh 1(드로우콜 2).
// 판 하나 = 바닥에 눕힌 사각형(가운데·향·반폭·반길이). 수명 동안 크기가 r0→r1로 바뀌고 셰이더가 종류·진행으로 모양을 그린다.
// 가산 종류: 충격 고리·열 원판·불길 띠(브레스가 지나간 줄)·치유 원(룬 눈금)·도발 고리(바깥 갈매기)·빛나는 균열.
// 알파 종류: 그을음 얼룩·흙먼지 고리·그을음 띠·어두운 균열. 프레임 루프 할당 없음(풀 + typed array).
import * as THREE from 'three/webgpu';
import * as TSL from 'three/tsl';

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any
const { abs, atan, attribute, float, fract, length, max, mix, sin, smoothstep, uniform, uv, vec2, vec3 } = TSL as unknown as Record<string, N>;

/** Target: 예고 원 위 조준 표식(바깥 고리 + 조여드는 안 고리 + 네 눈금 + 차오르는 용암빛), ConeEdge: 부채꼴 예고 가장자리 불길(seed = 반각 rad) */
export const GlowKind = { Shock: 0, Disc: 1, BurnStrip: 2, Heal: 3, Taunt: 4, Cracks: 5, Target: 6, ConeEdge: 7 } as const;
export const DarkKind = { Scorch: 0, DustRing: 1, ScorchStrip: 2, Cracks: 3 } as const;
/** 품질 배율 1 기준 상한 */
export const GROUND_CAP = { glow: 40, dark: 40 } as const;

/** x∈[-1,1], z∈[-1,1] 단위 사각형(uv.y가 +z로 는다 — 띠의 시작 0, 끝 1) */
function unitQuad(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1]), 3));
  g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array([0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0]), 3));
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
  g.setIndex([0, 2, 1, 0, 3, 2]);
  return g;
}

export class RaidGround {
  readonly mesh: THREE.InstancedMesh;
  readonly cap: number;
  dim = 1;
  private n = 0;
  private readonly x: Float32Array; private readonly z: Float32Array; private readonly ang: Float32Array;
  private readonly w0: Float32Array; private readonly w1: Float32Array; private readonly l0: Float32Array; private readonly l1: Float32Array;
  private readonly age: Float32Array; private readonly life: Float32Array; private readonly a0: Float32Array;
  private readonly kind: Uint8Array; private readonly seed: Float32Array; private readonly y: Float32Array;
  private readonly cr: Float32Array; private readonly cg: Float32Array; private readonly cb: Float32Array;
  /** 따라가는 판(도발 고리·치유 원): 매 프레임 x,z를 바꿀 수 있게 번호를 돌려준다(-1 = 없음) */
  private readonly id: Int32Array;
  private nextId = 1;
  private readonly aG: THREE.InstancedBufferAttribute;
  private readonly aCol: THREE.InstancedBufferAttribute;
  private readonly time = uniform(0);
  private rs = 0x6a09e667;

  constructor(readonly mode: 'glow' | 'dark', particleScale: number, name: string) {
    const base = mode === 'glow' ? GROUND_CAP.glow : GROUND_CAP.dark;
    this.cap = Math.max(12, Math.floor(base * Math.max(0.5, Math.min(1, particleScale))));
    const geo = unitQuad();
    this.aG = new THREE.InstancedBufferAttribute(new Float32Array(this.cap * 4), 4);
    this.aG.setUsage(THREE.DynamicDrawUsage);
    this.aCol = new THREE.InstancedBufferAttribute(new Float32Array(this.cap * 4), 4);
    this.aCol.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aG', this.aG);
    geo.setAttribute('aCol', this.aCol);
    this.mesh = new THREE.InstancedMesh(geo, mode === 'glow' ? glowMaterial(this.time) : darkMaterial(this.time), this.cap);
    this.mesh.name = name;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.visible = false;
    // 바닥 경고 데칼(순서 5)보다 위, 입자보다 아래. 그을음은 빛 판보다 먼저
    this.mesh.renderOrder = mode === 'glow' ? 8 : 6;
    const f = (): Float32Array => new Float32Array(this.cap);
    this.x = f(); this.z = f(); this.ang = f(); this.w0 = f(); this.w1 = f(); this.l0 = f(); this.l1 = f();
    this.age = f(); this.life = f(); this.a0 = f(); this.seed = f(); this.y = f();
    this.cr = f(); this.cg = f(); this.cb = f();
    this.kind = new Uint8Array(this.cap); this.id = new Int32Array(this.cap);
  }

  get active(): number { return this.n; }

  private rnd(): number { this.rs = (Math.imul(this.rs, 1664525) + 1013904223) >>> 0; return this.rs / 4294967296; }

  /**
   * 판 하나. 원형이면 half = 반지름(w = l). 띠는 (x,z)가 가운데, ang = 길이 방향(facing 규약), w = 반폭, l = 반길이.
   * 가득 차면 가장 늙은 판을 덮어쓴다. 돌려주는 번호로 move()·end()를 부른다
   */
  add(kind: number, x: number, z: number, ang: number, w0: number, w1: number, l0: number, l1: number, life: number, color: number, bright: number, alpha: number, seed = -1): number {
    let i: number;
    if (this.n < this.cap) i = this.n++;
    else {
      i = 0;
      for (let k = 1; k < this.n; k++) if (this.age[k] / this.life[k] > this.age[i] / this.life[i]) i = k;
    }
    this.kind[i] = kind; this.x[i] = x; this.z[i] = z; this.ang[i] = ang;
    this.w0[i] = w0; this.w1[i] = w1; this.l0[i] = l0; this.l1[i] = l1;
    this.age[i] = 0; this.life[i] = Math.max(0.05, life); this.a0[i] = alpha; this.seed[i] = seed >= 0 ? seed : this.rnd();
    this.cr[i] = (color >> 16 & 255) / 255 * bright; this.cg[i] = (color >> 8 & 255) / 255 * bright; this.cb[i] = (color & 255) / 255 * bright;
    // 겹친 판끼리 깜박이지 않게 높이를 조금씩 다르게
    this.y[i] = 0.045 + (this.nextId % 7) * 0.003;
    const id = this.nextId++;
    if (this.nextId > 0x3fffffff) this.nextId = 1;
    this.id[i] = id;
    return id;
  }

  private find(id: number): number { for (let i = 0; i < this.n; i++) if (this.id[i] === id) return i; return -1; }

  /** 따라가는 판 옮기기. 없으면 false */
  move(id: number, x: number, z: number): boolean { const i = this.find(id); if (i < 0) return false; this.x[i] = x; this.z[i] = z; return true; }

  /** 판을 남은 sec 안에 끝낸다(이미 더 짧으면 그대로) */
  end(id: number, sec: number): void {
    const i = this.find(id); if (i < 0) return;
    const left = this.life[i] - this.age[i];
    if (left > sec) this.life[i] = this.age[i] + sec;
  }

  private kill(i: number): void {
    const j = --this.n;
    if (i === j) return;
    this.kind[i] = this.kind[j]; this.x[i] = this.x[j]; this.z[i] = this.z[j]; this.ang[i] = this.ang[j];
    this.w0[i] = this.w0[j]; this.w1[i] = this.w1[j]; this.l0[i] = this.l0[j]; this.l1[i] = this.l1[j];
    this.age[i] = this.age[j]; this.life[i] = this.life[j]; this.a0[i] = this.a0[j]; this.seed[i] = this.seed[j]; this.y[i] = this.y[j];
    this.cr[i] = this.cr[j]; this.cg[i] = this.cg[j]; this.cb[i] = this.cb[j]; this.id[i] = this.id[j];
  }

  update(dt: number, time: number): void {
    this.time.value = time;
    const m = this.mesh.instanceMatrix.array as Float32Array, g = this.aG.array as Float32Array, c = this.aCol.array as Float32Array;
    let i = 0;
    while (i < this.n) {
      if (dt > 0) {
        this.age[i] += dt;
        if (this.age[i] >= this.life[i]) { this.kill(i); continue; }
      }
      const t = Math.min(1, this.age[i] / this.life[i]);
      const e = 1 - (1 - t) * (1 - t) * (1 - t);
      const w = this.w0[i] + (this.w1[i] - this.w0[i]) * e, l = this.l0[i] + (this.l1[i] - this.l0[i]) * e;
      const s = Math.sin(this.ang[i]), co = Math.cos(this.ang[i]);
      const o = i * 16;
      // 열: 오른쪽(cos, 0, -sin)·위·앞(sin, 0, cos)
      m[o] = co * w; m[o + 1] = 0; m[o + 2] = -s * w; m[o + 3] = 0;
      m[o + 4] = 0; m[o + 5] = 1; m[o + 6] = 0; m[o + 7] = 0;
      m[o + 8] = s * l; m[o + 9] = 0; m[o + 10] = co * l; m[o + 11] = 0;
      m[o + 12] = this.x[i]; m[o + 13] = this.y[i]; m[o + 14] = this.z[i]; m[o + 15] = 1;
      g[i * 4] = this.kind[i]; g[i * 4 + 1] = t; g[i * 4 + 2] = this.a0[i]; g[i * 4 + 3] = this.seed[i];
      const k = this.mode === 'glow' ? this.dim : 1;
      c[i * 4] = this.cr[i] * k; c[i * 4 + 1] = this.cg[i] * k; c[i * 4 + 2] = this.cb[i] * k; c[i * 4 + 3] = w / Math.max(1e-3, l);
      i++;
    }
    this.mesh.count = this.n;
    this.mesh.visible = this.n > 0;
    if (this.n > 0) { this.mesh.instanceMatrix.needsUpdate = true; this.aG.needsUpdate = true; this.aCol.needsUpdate = true; }
  }

  prewarm(on: boolean): void { this.mesh.count = on ? Math.max(1, this.n) : this.n; this.mesh.visible = on || this.n > 0; }
  clear(): void { this.n = 0; this.mesh.count = 0; this.mesh.visible = false; }
  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}

const is = (v: N, k: number): N => float(1).sub(smoothstep(float(0.25), float(0.5), abs(v.sub(k))));
const step01 = (v: N): N => smoothstep(float(0), float(0.02), v);
const band = (v: N, c: N, w: N, soft: N): N => float(1).sub(smoothstep(w, w.add(soft), abs(v.sub(c))));

/** 각도 갈래 잡음(균열·불길 가장자리) */
function rays(a: N, seed: N, n: number): N {
  return sin(a.mul(n).add(seed.mul(40))).mul(0.5).add(sin(a.mul(n * 2.3).sub(seed.mul(17))).mul(0.3)).add(sin(a.mul(n * 5.1).add(seed.mul(7))).mul(0.2));
}

function glowMaterial(time: N): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
  m.forceSinglePass = true; m.fog = false;
  const G: N = attribute('aG', 'vec4');
  const C: N = attribute('aCol', 'vec4');
  const kind = G.x, t = G.y, alpha = G.z, seed = G.w;
  const lx: N = uv().x.mul(2).sub(1), lz: N = uv().y.mul(2).sub(1);
  const r: N = length(vec2(lx, lz));
  const a: N = atan(lz, lx);
  const edge = float(1).sub(smoothstep(float(0.92), float(1), r));
  // 충격 고리: 바깥으로 번지며 얇아지는 고리 + 안쪽 옅은 빛
  const thick = mix(float(0.16), float(0.03), t);
  const shock = band(r, float(0.86), thick, float(0.08)).add(smoothstep(float(0.9), float(0.2), r).mul(0.18).mul(float(1).sub(t))).mul(edge).mul(float(1).sub(t).pow(1.4));
  // 열 원판: 가운데 밝은 원
  const disc = smoothstep(float(1), float(0), r).pow(1.6).mul(float(1).sub(t));
  // 불길 띠: lx = 폭, uv.y = 길이. 가장자리가 불꽃 혀처럼 출렁이고 결이 흐른다. 식으면서 잔불 점만 남는다
  const along: N = uv().y;
  const tongue = sin(along.mul(float(1).div(C.w.max(0.01)).mul(9)).add(time.mul(6)).add(seed.mul(20))).mul(0.08).add(sin(along.mul(37).sub(time.mul(9))).mul(0.05));
  const sideW = float(0.85).add(tongue);
  const inside = float(1).sub(smoothstep(sideW.sub(0.25), sideW, abs(lx)));
  const flick = fract(along.mul(float(1).div(C.w.max(0.01)).mul(3)).sub(time.mul(1.8)).add(sin(lx.mul(5).add(time.mul(4))).mul(0.15)));
  const hot = float(1).sub(t).pow(1.5);
  const spots = smoothstep(float(0.55), float(0.95), sin(along.mul(61).add(seed.mul(9))).mul(sin(lx.mul(13).add(along.mul(23)))));
  const ends = smoothstep(float(0), float(0.03), along).mul(float(1).sub(smoothstep(float(0.97), float(1), along)));
  const burn = inside.mul(ends).mul(hot.mul(float(0.55).add(flick.mul(0.45))).add(spots.mul(0.5).mul(float(1).sub(hot))));
  // 치유 원: 고리 + 안쪽 도는 룬 눈금 12개 + 옅은 면
  const ticks = smoothstep(float(0.6), float(0.9), sin(a.mul(12).add(time.mul(1.2))));
  const heal = band(r, float(0.9), float(0.025), float(0.04)).add(band(r, float(0.74), float(0.05), float(0.03)).mul(ticks)).add(smoothstep(float(0.95), float(0), r).mul(0.16))
    .mul(edge).mul(smoothstep(float(0), float(0.12), t)).mul(float(1).sub(smoothstep(float(0.7), float(1), t)));
  // 도발 고리: 굵게 박동하는 고리 + 바깥으로 향한 갈매기 8개
  const pulse = sin(time.mul(7)).mul(0.5).add(0.5);
  const aw = fract(a.div(Math.PI / 4)).sub(0.5).abs();
  const chevR = float(0.66).add(aw.mul(0.35)).add(fract(time.mul(0.9)).mul(0.12));
  const taunt = band(r, float(0.92), float(0.035), float(0.05)).mul(float(0.7).add(pulse.mul(0.3))).add(band(r, chevR, float(0.025), float(0.03)).mul(0.8))
    .mul(edge).mul(float(1).sub(smoothstep(float(0.8), float(1), t)));
  // 빛나는 균열: 가운데서 뻗는 가는 금 + 끝으로 갈수록 가늘고 식는다
  const crackA = abs(rays(a, seed, 7)).add(r.mul(0.15));
  const crackLine = float(1).sub(smoothstep(float(0.03), float(0.09), abs(fract(crackA.mul(3)).sub(0.5)).mul(r.add(0.3))));
  const cracks = crackLine.mul(smoothstep(float(1), float(0.2), r)).mul(smoothstep(float(0.05), float(0.18), r)).mul(float(1).sub(t).pow(1.2));
  // 조준 표식: 바깥 고리(빨라지는 맥동) + 안 고리가 가장자리로 조여든다 + 네 눈금 + 가운데 용암빛이 차오른다(t = 경고 진행)
  const beat = sin(time.mul(mix(float(6), float(22), t))).mul(0.5).add(0.5);
  const inner = mix(float(0.25), float(0.86), t.pow(1.6));
  const tick4 = float(1).sub(smoothstep(float(0.05), float(0.09), abs(fract(a.div(Math.PI / 2).add(0.125)).sub(0.5)).mul(r.add(0.2))));
  // 읽기 쉬운 타이밍(L11): 차오르는 면이 진행만큼 진해지고(최대 0.6), 앞선 고리가 끝으로 갈수록 밝아지며,
  // 마지막 0.2초에는 바깥 고리가 굵고 하얗게 달아오른다(= 곧 터짐). 한 경고에 한 번이라 섬광 횟수 규칙과 무관한 그 자리 빛이다
  const ripe = smoothstep(float(0.8), float(0.97), t);
  const target = band(r, float(0.885), mix(float(0.018), float(0.05), ripe), float(0.03)).mul(float(0.6).add(beat.mul(0.4)).add(ripe.mul(1.3)))
    .add(band(r, inner, float(0.026), float(0.03)).mul(float(0.9).add(t.mul(0.9))))
    .add(tick4.mul(band(r, float(0.76), float(0.09), float(0.03))).mul(0.8))
    .add(smoothstep(inner, float(0), r).mul(t.pow(1.5)).mul(0.6))
    .mul(edge);
  // 부채꼴 가장자리: 호(r≈0.97)와 옆선 두 개에 불길이 흐른다. 꼭짓점은 비운다(보스 몸 아래)
  const half: N = seed;
  const ca: N = atan(lx, lz).abs();
  const insideC = float(1).sub(smoothstep(half, half.add(0.03), ca));
  const arcRim = band(r, float(0.96), float(0.02), float(0.03)).mul(insideC);
  const sideLine = float(1).sub(smoothstep(float(0.012), float(0.03), abs(ca.sub(half)).mul(r))).mul(smoothstep(float(0.25), float(0.45), r)).mul(step01(float(1).sub(r)));
  const coneFlow = fract(r.mul(4).sub(time.mul(2.2))).mul(0.5).add(0.5);
  const coneEdge = arcRim.add(sideLine.mul(coneFlow)).mul(float(0.55).add(t.mul(0.45)));
  const shape = shock.mul(is(kind, 0)).add(disc.mul(is(kind, 1))).add(burn.mul(is(kind, 2))).add(heal.mul(is(kind, 3))).add(taunt.mul(is(kind, 4))).add(cracks.mul(is(kind, 5)))
    .add(target.mul(is(kind, 6))).add(coneEdge.mul(is(kind, 7)));
  // 불길 띠는 뜨거울 때 노랗게, 식으면 붉게
  const burnCol = mix(C.xyz.mul(vec3(1.0, 0.45, 0.25)), C.xyz.mul(vec3(1.1, 1.05, 0.8)), hot.mul(flick.mul(0.5).add(0.5)));
  // 조준 표식이 익으면(곧 터짐) 같은 밝기의 흰빛 쪽으로 바랜다
  m.colorNode = mix(mix(C.xyz, burnCol, is(kind, 2)), vec3(C.x, C.x.mul(0.92), C.x.mul(0.75)), ripe.mul(is(kind, 6)).mul(0.65));
  m.opacityNode = shape.mul(alpha).clamp(0, 1);
  return m;
}

function darkMaterial(time: N): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
  m.forceSinglePass = true;
  const G: N = attribute('aG', 'vec4');
  const C: N = attribute('aCol', 'vec4');
  const kind = G.x, t = G.y, alpha = G.z, seed = G.w;
  const lx: N = uv().x.mul(2).sub(1), lz: N = uv().y.mul(2).sub(1);
  const r: N = length(vec2(lx, lz));
  const a: N = atan(lz, lx);
  const out = float(1).sub(t).pow(0.7).mul(smoothstep(float(0), float(0.06), t));
  // 그을음 얼룩: 가장자리를 갈래 잡음으로 흔든 원, 안쪽이 더 진하다
  const blotR = float(0.8).add(rays(a, seed, 5).mul(0.12));
  const scorch = float(1).sub(smoothstep(blotR.sub(0.35), blotR, r)).mul(float(0.55).add(smoothstep(blotR, float(0), r).mul(0.45)));
  // 흙먼지 고리: 넓고 무른 고리
  const dust = band(r, float(0.72), float(0.12), float(0.2)).mul(float(1).sub(t).pow(1.3)).mul(0.8);
  // 그을음 띠: 폭 lx, 길이 uv.y. 가장자리를 흔든다
  const along: N = uv().y;
  const sw = float(0.78).add(sin(along.mul(float(1).div(C.w.max(0.01)).mul(7)).add(seed.mul(30))).mul(0.1)).add(sin(along.mul(41)).mul(0.04));
  const strip = float(1).sub(smoothstep(sw.sub(0.3), sw, abs(lx))).mul(smoothstep(float(0), float(0.04), along)).mul(float(1).sub(smoothstep(float(0.96), float(1), along)));
  // 어두운 균열
  const crackA = abs(rays(a, seed, 7)).add(r.mul(0.15));
  const crack = float(1).sub(smoothstep(float(0.04), float(0.12), abs(fract(crackA.mul(3)).sub(0.5)).mul(r.add(0.3)))).mul(smoothstep(float(1), float(0.25), r)).mul(smoothstep(float(0.05), float(0.15), r));
  const shape = scorch.mul(is(kind, 0)).add(dust.mul(is(kind, 1))).add(strip.mul(is(kind, 2))).add(crack.mul(is(kind, 3)));
  m.colorNode = C.xyz.mul(max(float(0.5), float(1).sub(r.mul(0.3))));
  m.opacityNode = shape.mul(alpha).mul(mix(out, float(1).sub(t).pow(1.3), is(kind, 1))).clamp(0, 1);
  void time;
  return m;
}
